import { useMemo } from "react";
import { components, paths } from "@core/api/api";
import useAxios from "@hooks/useFetch";
import { CommonDataKeywordItem } from "./useCommonData";
import { useCommonDataContext, useGetHeatScore, useGetMacStage, useMetrics } from "@core/CommonDataContext";
import {
    compareMetrics,
    Metric,
    TREND_SUMMARY_METRICS_ORDER
} from "@models/Metric";

const TREND_SUMMARY_ENDPOINT: keyof paths = '/api/Trend/GetTrendSummary';

export type TrendSummaryRequest = components["schemas"]["FlavorTracker.Entities.DTO.Common.AmongDropDownBaseRequest"];
export type TrendSummaryResponseItem = components["schemas"]["FlavorTracker.Entities.DTO.TrendPage.TrendSummaryData"];
export type TrendSummaryItem = Omit<TrendSummaryResponseItem, 'metrics'> & {
    name: CommonDataKeywordItem["name"];
    macStage: CommonDataKeywordItem["macStage"] | null;
    macStageTitle: string;
    heatScore: CommonDataKeywordItem["heatScore"];
    heatScoreTitle: string;
    heatScoreNum: number | null;
    subCategoriesIds: CommonDataKeywordItem["subCategoriesIds"];
    metrics: {
        id: Metric;
        name: string;
        value: string;
        numValue: number | null;
        changeValue: string;
        changeValueSigned: string;
        changeNumValue: number | null;
    }[];
};
export type TrendSummaryResponseRaw = TrendSummaryResponseItem[];

export type TrendSummaryResponse = TrendSummaryItem[];


export function useTrendSummary(payload: TrendSummaryRequest): {
    isLoading: boolean;
    data: TrendSummaryResponse;
} {
    const response = useAxios<TrendSummaryResponseRaw, TrendSummaryRequest>({
        url: TREND_SUMMARY_ENDPOINT,
        method: 'post',
        payload,
    });

    const { keywords = [] } = useCommonDataContext();
    const rawMetrics = useMetrics();
    const getMacStage = useGetMacStage();
    const getHeatScore = useGetHeatScore();
    const metrics = useMemo(
        () => (rawMetrics ?? [])
            .filter((i) => TREND_SUMMARY_METRICS_ORDER.includes(i.id as Metric))
            .sort((a, b) => compareMetrics(TREND_SUMMARY_METRICS_ORDER, a.id, b.id)),
        [rawMetrics],
    );

    const data = useMemo(
        (): TrendSummaryResponse => {
            return (response.data ?? []).map((item) => {
                const id = item.id!;
                const keyword = keywords[id] ?? null;

                const knowItMetric = item.metrics?.find(m => m.metric === Metric.KnowIt);
                const knowItOriginalChange = knowItMetric?.originalChange ?? null;

                const heatScoreNum = (keyword?.heatScore && knowItOriginalChange !== null)
                    ? keyword?.heatScore * 1000 + knowItOriginalChange
                    : null;

                return {
                    ...item,
                    name: (keyword?.name ?? '').toUpperCase(),
                    macStage: keyword?.macStage === 0 ? null : keyword?.macStage,
                    macStageTitle: getMacStage(keyword?.macStage)?.name ?? '-',
                    heatScore: keyword?.heatScore,
                    heatScoreNum,
                    heatScoreTitle: getHeatScore(keyword?.heatScore)?.name ?? '-',
                    subCategoriesIds: keyword?.subCategoriesIds ?? [],
                    metrics: metrics.map(m => {
                        const metricValue = item.metrics?.find(
                            (o) => o.metric === m.id
                        );

                        const rawValue = metricValue?.raw ?? null;
                        const change = metricValue?.change ?? null;
                        const changeSign = (metricValue?.change ?? 0) > 0 ? '+' : ''

                        return {
                            id: m.id ?? Metric.NoData,
                            name: m.name ?? '',
                            value: rawValue === null ? '-' : `${rawValue}%`,
                            numValue: metricValue?.originalRaw ?? null,
                            changeValue: change === null ? '-' : `${change}%`,
                            changeValueSigned: change === null ? '-' : `${changeSign}${change}%`,
                            changeNumValue: metricValue?.originalChange ?? null,
                        };
                    }),
                };
            });
        },
        [response.data, keywords, metrics, getMacStage, getHeatScore]
    );

    return {
        ...response,
        data,
    };
} 